import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { PairScoreView, Retailer } from '../../generated';
import { searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet } from '../../generated';
import { COLLECTION_SCORING_WINDOW_MS } from '../../utils/poll-while-pending';

/*
 * One image's `pair_score`, read from the collection images listing
 * (GET /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/images) filtered to that image and
 * asked for one (benchmark, product category) pair. Tests answer this path with the MSW handler in
 * `tests/mocks/handlers.ts`.
 */

/**
 * An image's score for one (benchmark, product category) pair, the generated type also on `ImageView.pair_score`.
 * `scored` is `false` while the pair is unscored, was never requested, or its pool is empty.
 */
export type { PairScoreView };

/** How an image reads under a pair, shared by the collection tiles and the asset header so both agree. */
export interface PairScoreReading {
  /** The pair's score once it is scored, else `null` */
  score: number | null;
  /** No pair score for the image yet, or the pair's scoring is still running for it */
  isScoring: boolean;
  /** The pair settled without scoring the image; only a new scoring of the collection can change that */
  isSettledWithoutScore: boolean;
  /** A fresh upload still waiting for its first pair score, counted as scoring whatever the pair's status says */
  isAwaitingUpload: boolean;
}

export interface ReadPairScoreOptions {
  /**
   * The page saw the image uploaded and is still waiting on its score.
   * @default false
   */
  isAwaitingUpload?: boolean;
  /** When the image was created, by the API's clock; see `isUploadedAfterPairRequest` for the window */
  imageCreatedAt?: string | null;
  /** When the pair's scoring was last requested, by the API's clock */
  pairRequestedAt?: string | null;
  /**
   * The pair score was read before the pair's current request. Until it is read again only a score counts; anything
   * else waits, as the new run may not have reached the image yet.
   * @default false
   */
  isReadBeforeRequest?: boolean;
}

/**
 * Whether the image was uploaded after the pair was last requested, within the scoring window since. The API reopens
 * the pair for an upload from its landing, seconds later and with a new request time, so until then the pair's
 * "ready" or "failed" is not about this image yet.
 *
 * The two timestamps are compared on the API's clock; the window is measured from the upload by the client's, like
 * every `pollWhilePending` anchor, so it shifts by the client's skew: a client ahead by more than the window reads the
 * upload as settled at once (as before this rule), one behind waits that much longer.
 */
export function isUploadedAfterPairRequest(imageCreatedAt?: string | null, pairRequestedAt?: string | null): boolean {
  if (!imageCreatedAt || !pairRequestedAt) {
    return false;
  }
  const createdAtMs = Date.parse(imageCreatedAt);
  return createdAtMs > Date.parse(pairRequestedAt) && Date.now() - createdAtMs <= COLLECTION_SCORING_WINDOW_MS;
}

/**
 * Reads an image's score under a pair. A fresh upload without a score yet, one the page saw uploaded or one created
 * after the pair was last requested, counts as scoring whatever the pair's status says, and so does an unscored read
 * taken before the pair's current request.
 */
export function readPairScore(
  pairScore: PairScoreView | null | undefined,
  options: ReadPairScoreOptions = {},
): PairScoreReading {
  const score = pairScore?.scored ? pairScore.vizit_score : null;
  const isAwaitingUpload =
    !pairScore?.scored &&
    (!!options.isAwaitingUpload || isUploadedAfterPairRequest(options.imageCreatedAt, options.pairRequestedAt));
  const isScoring =
    !pairScore ||
    (pairScore.scoring_status === 'pending' && !pairScore.scored) ||
    isAwaitingUpload ||
    (!!options.isReadBeforeRequest && !pairScore.scored);
  return { score, isScoring, isSettledWithoutScore: !isScoring && score === null, isAwaitingUpload };
}

export interface GetCollectionImagePairScoreQueryVariables {
  workspace_id: string;
  collection_id: string;
  image_id: string;
  product_category_id: string;
  /** Exactly one of `benchmark_id` (custom) or `retailer` (Vizit) names the benchmark. */
  benchmark_id?: string;
  retailer?: Retailer;
}

/** One read of the image's pair score, with the pair's request time the read was started under. */
export interface GetCollectionImagePairScoreQuery {
  /** The image's score under the pair, `null` when the listing has none for it */
  pairScore: PairScoreView | null;
  /** The pair's `requested_at` the page knew when the read started, `null` when it named none */
  requestedAt: string | null;
}

export interface UseGetCollectionImagePairScoreQueryOptions<TData, TError>
  extends Omit<UseQueryOptions<GetCollectionImagePairScoreQuery, TError, TData>, 'queryKey' | 'queryFn' | 'meta'> {
  /**
   * The pair's current `requested_at`. Not sent and not in the key: each read records the value it started under, so
   * a read from before a later request can be told apart from one after it.
   * @type {string | null}
   * @optional
   */
  requestedAt?: string | null;
}

async function getCollectionImagePairScore(variables: GetCollectionImagePairScoreQueryVariables) {
  const { workspace_id, collection_id, image_id, product_category_id, benchmark_id, retailer } = variables;
  const { data } = await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
    path: { workspace_id, collection_id },
    query: { image_ids: [image_id], product_category_id, benchmark_id, retailer, limit: 1 },
    throwOnError: true,
  });
  return data.find((image) => image.id === image_id)?.pair_score ?? null;
}

export const useGetCollectionImagePairScoreQuery = <TData = GetCollectionImagePairScoreQuery, TError = unknown>(
  variables: GetCollectionImagePairScoreQueryVariables,
  options: UseGetCollectionImagePairScoreQueryOptions<TData, TError> = {},
) => {
  const { requestedAt = null, ...queryOptions } = options;
  return useQuery<GetCollectionImagePairScoreQuery, TError, TData>({
    queryKey: useGetCollectionImagePairScoreQuery.getKey(variables),
    // The query's meta follows the latest render, so a read takes the request time the page knew when it started.
    queryFn: async ({ meta }) => {
      const startedUnder = (meta?.requestedAt as string | null | undefined) ?? null;
      return { pairScore: await getCollectionImagePairScore(variables), requestedAt: startedUnder };
    },
    meta: { requestedAt },
    ...queryOptions,
  });
};

useGetCollectionImagePairScoreQuery.getKey = (variables: GetCollectionImagePairScoreQueryVariables) => [
  'GetCollectionImagePairScore',
  variables,
];

useGetCollectionImagePairScoreQuery.fetcher = (variables: GetCollectionImagePairScoreQueryVariables) => () =>
  getCollectionImagePairScore(variables);
